// CR121: Auto Mode Types for Batch Run Step Group Orchestration
// CR122: Extended to include S6/S7 publish steps
// CR502: Moved S5_CROP into S1 pipeline as S1_5 (compat-only, deprecated)
import { resolveFlyerCaptureMode } from "./flyerCaptureMode";

/**
 * Step group identifiers for auto mode orchestration
 * - S1: Flyer extraction steps (1.0-1.5)
 * - S2: Website Collect
 * - S4: Normalize All Staging
 * - S5: Dedup All Normalized
 * - S5_CROP: (DEPRECATED — alias for S1_5, kept for historical runs)
 * - S6: Review Deduped Deals (CR122)
 * - S6_5: Detect Reward Brand Merchants (CR319)
 * - S7: Publish to Production (CR122)
 */
export type StepGroupKey = "S0.5" | "S1" | "S2" | "S4" | "S5" | "S5_CROP" | "S6" | "S6_5" | "S7";

/**
 * Note: S5_CROP is retained as a type alias for backward compatibility.
 * CR502: S1_5 is the new step key for legacy snippet cropping (compat-only, deprecated).
 * New snippet locator work uses S1_4 (S1.4) in the Flyer Extract phase. S1_SNIPPET is a legacy alias for S1_4.
 */

/**
 * Status for individual step groups
 */
export type StepGroupStatus = "PENDING" | "RUNNING" | "COMPLETE" | "FAILED" | "SKIPPED" | "WAITING" | "READY";

/**
 * Substep keys for S1 flyer pipeline
 */
export type S1SubstepKey = "S1_COLLECT" | "S1_RENDER" | "S1_OCR" | "S1_AI" | "S1_4" | "S1_5";

/**
 * Auto mode configuration stored in config_json
 */
export interface AutoModeConfig {
  /** Top-level batch auto-run switch (S1→S2→S4→S5). */
  batch_auto_run_enabled?: boolean;
  /** Auto mode enabled per step group */
  auto_mode: {
    "S0.5": boolean;
    S1: boolean;
    S2: boolean;
    S4: boolean;
    S5: boolean;
    S5_CROP: boolean; // CR502: DEPRECATED alias — resolves to S1_5
    S6: boolean;  // CR122: Review auto-complete
    S6_5: boolean; // CR319: Reward brand detection
    S7: boolean;  // CR122: Publish auto-trigger
  };
  /** Global policies */
  auto_policy: {
    /** Stop chaining on any step failure */
    stop_on_failure: boolean;
    /** Maximum time to wait for a step (ms) - 0 = no limit */
    step_timeout_ms: number;
  };
  /** Runtime state (updated during execution) */
  auto_state?: {
    /** Currently running step group */
    running_group: StepGroupKey | null;
    /** Currently running substep (for S1) */
    running_substep: S1SubstepKey | null;
    /** When auto mode was last triggered */
    last_triggered_at: string | null;
    /** Last error message */
    last_error: string | null;
  };
  /** Persisted batch auto-run runtime state for reload continuity. */
  batch_auto_run_state?: {
    active: boolean;
    current_step_key: StepGroupKey | null;
    last_step_key: StepGroupKey | null;
    last_error: string | null;
    started_at: string | null;
  };
}

/**
 * Default auto mode config
 */
export const DEFAULT_AUTO_MODE_CONFIG: AutoModeConfig = {
  batch_auto_run_enabled: false,
  auto_mode: {
    "S0.5": false,
    S1: false,
    S2: false,
    S4: false,
    S5: false,
    S5_CROP: false,
    S6: false,
    S6_5: false,
    S7: false,
  },
  auto_policy: {
    stop_on_failure: true,
    step_timeout_ms: 0,
  },
  auto_state: {
    running_group: null,
    running_substep: null,
    last_triggered_at: null,
    last_error: null,
  },
  batch_auto_run_state: {
    active: false,
    current_step_key: null,
    last_step_key: null,
    last_error: null,
    started_at: null,
  },
};

/**
 * Step group display info
 */
export interface StepGroupInfo {
  key: StepGroupKey;
  name: string;
  description: string;
  substeps?: { key: string; name: string }[];
}

/**
 * Step group definitions with display names and substeps
 */
export const STEP_GROUPS: StepGroupInfo[] = [
  {
    key: "S0.5",
    name: "S0.5: Download Capture",
    description: "Download flyer PDFs and/or capture screenshots",
  },
  {
    key: "S1",
    name: "S1: Flyer Extraction",
    description: "Collect, render, OCR, and AI extract from flyers",
    substeps: [
      { key: "S1_COLLECT", name: "1.0 Collect Flyers" },
      { key: "S1_RENDER", name: "1.1 Render Pages" },
      { key: "S1_OCR", name: "1.2 OCR + Candidates" },
      { key: "S1_AI", name: "1.4 Deep Scan" },
      { key: "S1_4", name: "1.5 Locate & Crop" },
      { key: "S1_5", name: "1.5 Legacy Snippet Crop (Deprecated)" },
    ],
  },
  {
    key: "S2",
    name: "S2: Website Collect",
    description: "Scrape merchant websites for deals",
  },
  {
    key: "S4",
    name: "S4: Normalize",
    description: "Normalize all staged deals",
  },
  {
    key: "S5",
    name: "S5: Dedup",
    description: "Deduplicate normalized deals",
  },
  // CR502: S5_CROP removed from main list — now lives as S1.5 substep (compat-only)
  {
    key: "S6",
    name: "S6: Review",
    description: "Review deduped deals before publishing",
  },
  {
    key: "S6_5",
    name: "S6.5: Detect Reward Brands",
    description: "Scan accepted deals for reward brands and create/link merchants",
  },
  {
    key: "S7",
    name: "S7: Publish",
    description: "Publish accepted deals to production",
  },
];

/**
 * Ordered sequence for cross-group chaining
 * CR502: S5_CROP removed — legacy snippet crop is now S1_5 (compat-only, not auto-chained)
 */
export const STEP_GROUP_SEQUENCE: StepGroupKey[] = ["S0.5", "S1", "S2", "S4", "S5", "S6", "S6_5", "S7"];
export const PHASE1_STEP_GROUP_SEQUENCE: StepGroupKey[] = ["S0.5", "S1", "S2", "S4", "S5"];

export function getPhase1RunChainSequence(
  flyerCaptureMode: string | null | undefined,
  sourcesEnabled: string[] = [],
): StepGroupKey[] {
  let sequence = [...PHASE1_STEP_GROUP_SEQUENCE];

  const normalizedMode = typeof flyerCaptureMode === "string" ? flyerCaptureMode.toUpperCase() : null;
  if (normalizedMode === "IMAGE_CAPTURE") {
    sequence = sequence.filter((group) => group !== "S0.5");
  } else if (normalizedMode === "DOWNLOAD_ONLY") {
    sequence = sequence.filter((group) => group !== "S1");
  }

  const flyerEnabled = sourcesEnabled.includes("FLYER");
  const webEnabled = sourcesEnabled.includes("WEB");

  if (!flyerEnabled) {
    sequence = sequence.filter((group) => group !== "S0.5" && group !== "S1");
  }
  if (!webEnabled) {
    sequence = sequence.filter((group) => group !== "S2");
  }

  return sequence;
}

/**
 * S1 substep sequence
 * CR502: S1_5 added after S1_SNIPPET (compat-only, deprecated — auto-mode skips it)
 */
export const S1_SUBSTEP_SEQUENCE: S1SubstepKey[] = ["S1_COLLECT", "S1_RENDER", "S1_OCR", "S1_AI", "S1_4", "S1_5"];

/**
 * CR502: Map legacy S5_CROP step key to new S1_5 for alias resolution
 */
export function resolveStepKeyAlias(stepKey: string): string {
  if (stepKey === "S5_CROP" || stepKey === "S5_1" || stepKey === "S5.1") return "S1_5";
  if (stepKey === "S1_SNIPPET") return "S1_4";
  return stepKey;
}

/**
 * CR502: Check if a step is compat-only (deprecated, not runnable from UI)
 */
export function isCompatOnlyStep(stepKey: string): boolean {
  return stepKey === "S1_5" || stepKey === "S5_CROP" || stepKey === "S1_SNIPPET";
}

/**
 * Extract auto mode config from batch run config_json
 */
export function getAutoModeConfig(configJson: Record<string, unknown> | null | undefined): AutoModeConfig {
  const stored = configJson?.auto_mode_config as Partial<AutoModeConfig> | undefined;
  if (!stored) {
    return { ...DEFAULT_AUTO_MODE_CONFIG };
  }

  const storedAutoMode = (stored.auto_mode ?? {}) as Partial<AutoModeConfig["auto_mode"]>;
  const hasExplicitS05 = Object.prototype.hasOwnProperty.call(storedAutoMode, "S0.5");

  const mergedAutoMode: AutoModeConfig["auto_mode"] = {
    ...DEFAULT_AUTO_MODE_CONFIG.auto_mode,
    ...storedAutoMode,
  };

  // Backward compatibility: older batch configs omitted S0.5 in auto_mode defaults.
  // For IMAGE_CAPTURE runs, S0.5 should remain disabled by default.
  // Otherwise (download/upload/legacy), inherit phase-1 intent from S1/S2/S4/S5.
  if (!hasExplicitS05) {
    const resolvedCaptureMode = resolveFlyerCaptureMode(configJson);
    if (resolvedCaptureMode === "IMAGE_CAPTURE") {
      mergedAutoMode["S0.5"] = false;
    } else {
      const phase1IntentEnabled = Boolean(
        stored.batch_auto_run_enabled ||
        mergedAutoMode.S1 ||
        mergedAutoMode.S2 ||
        mergedAutoMode.S4 ||
        mergedAutoMode.S5
      );
      mergedAutoMode["S0.5"] = phase1IntentEnabled;
    }
  }
  
  // Merge with defaults to handle missing fields
  return {
    auto_mode: mergedAutoMode,
    auto_policy: {
      ...DEFAULT_AUTO_MODE_CONFIG.auto_policy,
      ...(stored.auto_policy || {}),
    },
    auto_state: {
      ...DEFAULT_AUTO_MODE_CONFIG.auto_state,
      ...(stored.auto_state || {}),
    },
    batch_auto_run_enabled:
      typeof stored.batch_auto_run_enabled === "boolean"
        ? stored.batch_auto_run_enabled
        : DEFAULT_AUTO_MODE_CONFIG.batch_auto_run_enabled,
    batch_auto_run_state: {
      ...DEFAULT_AUTO_MODE_CONFIG.batch_auto_run_state,
      ...(stored.batch_auto_run_state || {}),
    },
  };
}

/**
 * Helper to check if any step group has auto mode enabled
 */
export function hasAnyAutoModeEnabled(config: AutoModeConfig): boolean {
  return Object.values(config.auto_mode).some(Boolean);
}

/**
 * Get the next step group in the sequence
 */
export function getNextStepGroup(
  currentGroup: StepGroupKey,
  sequence: StepGroupKey[] = STEP_GROUP_SEQUENCE,
): StepGroupKey | null {
  const currentIndex = sequence.indexOf(currentGroup);
  if (currentIndex === -1 || currentIndex === sequence.length - 1) {
    return null;
  }
  return sequence[currentIndex + 1];
}

/**
 * Get the next S1 substep in the sequence
 */
export function getNextS1Substep(currentSubstep: S1SubstepKey): S1SubstepKey | null {
  const currentIndex = S1_SUBSTEP_SEQUENCE.indexOf(currentSubstep);
  if (currentIndex === -1 || currentIndex === S1_SUBSTEP_SEQUENCE.length - 1) {
    return null;
  }
  return S1_SUBSTEP_SEQUENCE[currentIndex + 1];
}
